import {
  createContext,
  PropsWithChildren,
  RefObject,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  FormattedLearnerSubscriptionPlan,
  Learner,
  ReferralCode,
} from "../../types";
import { noop } from "lodash";
import { executeQuery } from "../../../utils";
import { useParams, useSearchParams } from "react-router-dom";
import { useLearnerAnalytics } from "../../analytics/useLearnerAnalytics";
import {
  homepageScrollToPlansEvent,
  homepageSignupPaymentModalEvent,
} from "../../analytics/events";
import { useQuery } from "@tanstack/react-query";
import { ONE_MONTH_TRIAL_LENGTH } from "../../constants";

const SignupContext = createContext<{
  plansRef: RefObject<HTMLElement> | null;
  plans: FormattedLearnerSubscriptionPlan[] | undefined;
  selectedPlan: FormattedLearnerSubscriptionPlan | undefined;
  setSelectedPlan: (plan: FormattedLearnerSubscriptionPlan) => void;
  referral: ReferralCode | undefined;
  startTrialText: string;
  showSignup: boolean;
  setShowSignup: (show: boolean) => void;
  scrollToPlans: (locationOnPage: string) => void;
  accountType: "parent" | "learner" | undefined;
  setAccountType: (accountType: "parent" | "learner" | undefined) => void;
  numLearners: number;
  setNumLearners: (numLearners: number) => void;
  showLogin: boolean;
  setShowLogin: (show: boolean) => void;
  showPromoPrice: boolean;
  /**
   * Use this to force the payment view after an incomplete Google signup.
   * Normally payment displays automatically due to state changes after
   * signup+login, but an incomplete Google signup needs the user to edit their
   * name first which doesn't trigger the same state updates
   */
  showPayment: boolean;
  setShowPayment: (show: boolean) => void;
}>({
  plansRef: { current: null },
  plans: undefined,
  selectedPlan: undefined,
  setSelectedPlan: noop,
  referral: undefined,
  startTrialText: "",
  showSignup: false,
  setShowSignup: noop,
  scrollToPlans: noop,
  accountType: undefined,
  setAccountType: noop,
  numLearners: 1,
  setNumLearners: noop,
  setShowLogin: noop,
  showLogin: false,
  showPromoPrice: false,
  showPayment: false,
  setShowPayment: noop,
});

export function useHomepageSignupContext() {
  const ctx = useContext(SignupContext);
  return {
    ...ctx,
    // If someone sets a quantity and then signs up as a learner, only charge them for 1 learner
    numLearners: ctx.accountType === "learner" ? 1 : ctx.numLearners,
    numLearnersError: ctx.accountType === "learner" && ctx.numLearners > 1,
  };
}

export const HomepageSignupContextProvider: React.FC<
  PropsWithChildren<{
    plansRef: RefObject<HTMLElement> | null;
    initialAccountType?: "parent" | "learner";
    showPromoPrice?: boolean;
  }>
> = ({ plansRef, children, initialAccountType, showPromoPrice = false }) => {
  const { track } = useLearnerAnalytics();
  const user: Learner | null = JSON.parse(
    localStorage.getItem("user") || "null"
  );
  const [searchParams] = useSearchParams();
  const params = useParams();

  const { data: plansData } = useQuery<{
    plans: FormattedLearnerSubscriptionPlan[];
    referral?: ReferralCode;
  }>({
    queryKey: ["learnerPlans"],
    queryFn: () => {
      // Get referral code either from url, or from user if user signed up
      // with a referral code
      const referralCode = params.referralCode ?? user?.signupReferralCode;
      return executeQuery({
        path: "/payments/subscriptions/plans/list/published",
        params: referralCode ? { referralCode } : {},
      });
    },
    refetchOnWindowFocus: false,
  });
  const [selectedPlan, setSelectedPlan] = useState<
    FormattedLearnerSubscriptionPlan | undefined
  >(undefined);

  const startTrialText = !plansData
    ? ""
    : (plansData.referral?.trialPeriodDays ?? 0) === ONE_MONTH_TRIAL_LENGTH
    ? `Start 1 Month Free Trial`
    : (plansData.referral?.trialPeriodDays ?? 0) > 0
    ? `Start ${plansData.referral?.trialPeriodDays}-Day Free Trial`
    : (plansData.plans[0].trialLength ?? 0) > 0
    ? `Start ${plansData?.plans[0].trialLength}-Day Free Trial`
    : "Get Started";

  const [showSignup, setShowSignup] = useState(false);

  const [showLogin, setShowLogin] = useState<boolean>(
    searchParams.has("log-in") && searchParams.size === 1
  ); // show login modal on load when it is the only query param to avoid any un-intended states of the page

  const [accountType, setAccountType] = useState<
    "parent" | "learner" | undefined
  >(initialAccountType);

  const [numLearners, setNumLearners] = useState<number>(
    user?.subscriptionQuantity || 1
  );

  const [showPayment, setShowPayment] = useState(false);

  const scrollToPlans = (locationOnPage: string) => {
    plansRef?.current?.scrollIntoView({ behavior: "smooth" });
    track(homepageScrollToPlansEvent(locationOnPage));
  };

  const trackedSetShowSignup = (show: boolean) => {
    setShowSignup(show);
    track(homepageSignupPaymentModalEvent(show));
  };

  useEffect(() => {
    if (!plansData) {
      return;
    }
    if (searchParams.has("resume")) {
      let resumeData: any = {};
      try {
        resumeData = JSON.parse(
          window.atob(decodeURIComponent(searchParams.get("resume") || ""))
        );
      } catch (e) {
        // Ignore
      }

      if (user) {
        setAccountType(
          user?.entitlements?.includes("parent") ? "parent" : "learner"
        );
      } else if (resumeData.accountType) {
        setAccountType(resumeData.accountType);
      }

      if (resumeData.selectedPlan) {
        setSelectedPlan(
          plansData?.plans.find((plan) => plan._id === resumeData.selectedPlan)
        );
      } else {
        setSelectedPlan(plansData.plans[0]);
      }
      if (resumeData.numLearners) {
        setNumLearners(resumeData.numLearners);
      }
      setShowSignup(true);
    } else {
      setSelectedPlan(plansData.plans[0]);
    }
    if (user && searchParams.has("paid")) {
      setShowSignup(true);
    }
    // Only want this to run on first load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plansData]);

  return (
    <SignupContext.Provider
      value={{
        plansRef,
        plans: plansData?.plans,
        referral: plansData?.referral,
        selectedPlan,
        setSelectedPlan,
        startTrialText,
        showSignup,
        setShowSignup: trackedSetShowSignup,
        scrollToPlans,
        accountType,
        setAccountType,
        numLearners,
        setNumLearners,
        setShowLogin,
        showLogin,
        showPromoPrice,
        showPayment,
        setShowPayment,
      }}
    >
      {children}
    </SignupContext.Provider>
  );
};
